import BaseOfferCard from '@/components/purchase/card/BaseOfferCard';
import { TransText } from '@/i18n/trans/text';
import type { RootState } from '@/store';
import { useDispatch, useSelector } from '@/store/utils';
import type { SearchJourneyOfferDTO } from '@/types/dto/agent';
import type { OfferMapByLegId, OfferMapItem } from '@/types/offer';
import { isOfferSelectable } from '@/utils/purchase';
import type { ActionCreatorWithPayload, Selector } from '@reduxjs/toolkit';
import type { FC } from 'react';

interface OfferCardProps {
  offer: SearchJourneyOfferDTO;
  legId?: string;
  legIds?: string[];
  journeyDetails: {
    originStopName?: string;
    destinationStopName?: string;
  };
  updateSelectedOfferMap: ActionCreatorWithPayload<OfferMapItem>;
  selectedOfferMapByLegIdSelector: Selector<RootState, OfferMapByLegId>;
}

const OfferCard: FC<OfferCardProps> = ({
  offer,
  legId,
  legIds,
  updateSelectedOfferMap,
  selectedOfferMapByLegIdSelector,
}) => {
  const dispatch = useDispatch();
  const selectedOfferMapByLegId = useSelector(selectedOfferMapByLegIdSelector);
  const selectedOffer = legId ? selectedOfferMapByLegId[legId] : null;
  const isSelected = Boolean(selectedOffer?.id === offer.id);
  const isDisabled = !isOfferSelectable(offer);

  if (!offer) {
    return null;
  }

  const handleSelect = () => {
    if (!isDisabled && legId) {
      dispatch(
        updateSelectedOfferMap({
          legId,
          offer,
          legIds,
        })
      );
    }
  };

  return (
    <BaseOfferCard
      title={offer.productSummary || ''}
      amount={offer.exchangePrice?.amount ?? offer.price?.amount ?? 0}
      isSelected={isSelected}
      onSelect={handleSelect}
      descriptions={offer.productDescriptiveTexts
        ?.filter(
          (text) => text.textElementType === 'INFORMATION' && !!text.description
        )
        .map((text) => text.description!)}
      isRefundable={offer.refundable === 'YES'}
      isChangeable={offer.exchangeable === 'YES'}
      isDisabled={isDisabled}
      disabledText={<TransText i18nKey="NOT_ENOUGH_PLACES" />}
      showPassengerInfo
      appliedReduction={offer.appliedReductions?.[0]}
      data-testid="select-offer"
    />
  );
};

export default OfferCard;
